/**
 * Sync engine — pushes pending local changes to the server, and pulls
 * the full server state when the user requests it (or on first login).
 *
 * Design principles:
 * - Push is automatic but never aggressive: only on app start, after a new
 *   save, and on an explicit user trigger. No timers, no retries in a loop.
 * - Pull is manual: the user clicks a button (or it runs once at bootstrap).
 *   On pull, if local has pending writes that conflict with server settings,
 *   a conflict resolution callback is invoked so the UI can prompt.
 * - Workout sessions are append-only and keyed by client-generated UUID,
 *   so the server save is idempotent by id and conflicts cannot arise.
 */
import { hc } from 'hono/client'
import type { SyncApi } from '@backend/api/sync'
import type { WorkoutApi } from '@backend/api/stores/WorkoutStore'
import type { SettingsApi } from '@backend/api/stores/SettingsStore'
import type { CustomExerciseApi } from '@backend/api/stores/CustomExerciseStore'
import type { WeeklyPlanApi } from '@backend/api/stores/WeeklyPlanStore'
import { apiFetch } from './apiFetch'
import {
  db, setUser, markBootstrapped, isBootstrapped,
  getWeeklyPlan, setWeeklyPlan,
  type LocalSession, type LocalExerciseSettings, type LocalUserExercise,
  type LocalCustomExercise, type LocalWeeklyPlan,
} from './db'

const syncApi = hc<SyncApi>('/api/sync', { fetch: apiFetch })
const workoutApi = hc<WorkoutApi>('/api/WorkoutStore', { fetch: apiFetch })
const settingsApi = hc<SettingsApi>('/api/SettingsStore', { fetch: apiFetch })
const customApi = hc<CustomExerciseApi>('/api/CustomExerciseStore', { fetch: apiFetch })
const weeklyPlanApi = hc<WeeklyPlanApi>('/api/WeeklyPlanStore', { fetch: apiFetch })

// ============================================================================
// Login gate — sync only runs when user is logged in.
// LoginStore maintains this flag via setLoggedIn() on sign-in / sign-out /
// session-fetch. Guest mode keeps writes local-only.
// ============================================================================

let _isLoggedIn = false

export function setLoggedIn(value: boolean): void {
  _isLoggedIn = value
}

export function isLoggedIn(): boolean {
  return _isLoggedIn
}

// ============================================================================
// Push — sends pending local changes to the server
// ============================================================================

async function pushPendingSessions(): Promise<{ synced: number; failed: number }> {
  const pending = await db.sessions.where('syncStatus').equals('pending').toArray()
  let synced = 0
  let failed = 0
  for (const s of pending) {
    try {
      if (s.pendingDelete) {
        // Locally deleted — propagate to server then drop the row.
        const res = await workoutApi.delete.$post({ json: { id: s.id } })
        if (!res.ok) {
          failed++
          continue
        }
        await db.sessions.delete(s.id)
        synced++
        continue
      }
      if (s.pendingEdit) {
        // Row already exists upstream; /save is a no-op for known ids, so
        // patch the timing fields explicitly.
        const res = await workoutApi.update.$post({
          json: {
            id: s.id,
            startedAt: s.startedAt,
            endedAt: s.endedAt,
            durationSeconds: s.durationSeconds,
          },
        })
        if (!res.ok) {
          failed++
          continue
        }
        await db.sessions.update(s.id, { syncStatus: 'synced', pendingEdit: false })
        synced++
        continue
      }
      const res = await workoutApi.save.$post({
        json: {
          id: s.id,
          exerciseKey: s.exerciseKey ,
          exerciseType: s.exerciseType,
          startedAt: s.startedAt,
          endedAt: s.endedAt,
          durationSeconds: s.durationSeconds,
          summary: s.summary ?? undefined,
        },
      })
      if (!res.ok) {
        failed++
        continue
      }
      await db.sessions.update(s.id, { syncStatus: 'synced' })
      synced++
    } catch {
      failed++
      // Stop on network failure — everything else will fail too
      break
    }
  }
  return { synced, failed }
}

async function pushPendingExercises(): Promise<void> {
  const pending = await db.userExercises.where('syncStatus').equals('pending').toArray()
  for (const ex of pending) {
    try {
      if (ex.pendingDelete) {
        const res = await settingsApi.removeExercise.$post({
          json: { exerciseKey: ex.exerciseKey  },
        })
        if (res.ok) await db.userExercises.delete(ex.exerciseKey)
      } else {
        const res = await settingsApi.addExercise.$post({
          json: { exerciseKey: ex.exerciseKey  },
        })
        if (res.ok) await db.userExercises.update(ex.exerciseKey, { syncStatus: 'synced' })
      }
    } catch {
      break
    }
  }
}

async function pushPendingCustomExercises(): Promise<void> {
  const pending = await db.customExercises.where('syncStatus').equals('pending').toArray()
  for (const c of pending) {
    try {
      if (c.pendingDelete) {
        const res = await customApi.delete.$post({
          json: { exerciseKey: c.exerciseKey },
        })
        if (res.ok) await db.customExercises.delete(c.exerciseKey)
      } else {
        const res = await customApi.save.$post({
          json: {
            exerciseKey: c.exerciseKey,
            name: c.name,
            icon: c.icon,
            template: c.template,
            exerciseType: c.exerciseType,
            restDuration: c.restDuration ?? null,
            defaultDuration: c.defaultDuration ?? null,
            weight: c.weight ?? null,
            quickReps1: c.quickReps1 ?? null,
            quickReps2: c.quickReps2 ?? null,
            quickReps3: c.quickReps3 ?? null,
            sortOrder: c.sortOrder,
          },
        })
        if (res.ok) await db.customExercises.update(c.exerciseKey, { syncStatus: 'synced' })
      }
    } catch {
      break
    }
  }
}

async function pushPendingSettings(): Promise<void> {
  const pending = await db.exerciseSettings.where('syncStatus').equals('pending').toArray()
  for (const s of pending) {
    try {
      const res = await settingsApi.updateSettings.$post({
        json: {
          exerciseKey: s.exerciseKey ,
          plankDuration: s.plankDuration ?? undefined,
          plankRestDuration: s.plankRestDuration ?? undefined,
          curlRestDuration: s.curlRestDuration ?? undefined,
          curlWeight: s.curlWeight ?? undefined,
          pushUpRestDuration: s.pushUpRestDuration ?? undefined,
          dumbbellPressRestDuration: s.dumbbellPressRestDuration ?? undefined,
          dumbbellPressWeight: s.dumbbellPressWeight ?? undefined,
          gluteBridgeRestDuration: s.gluteBridgeRestDuration ?? undefined,
          squatRestDuration: s.squatRestDuration ?? undefined,
        },
      })
      if (res.ok) await db.exerciseSettings.update(s.exerciseKey, { syncStatus: 'synced' })
    } catch {
      break
    }
  }
}

async function pushPendingWeeklyPlan(): Promise<void> {
  const plan = await getWeeklyPlan()
  if (plan.syncStatus !== 'pending') return
  try {
    const res = await weeklyPlanApi.save.$post({
      json: {
        mon: plan.mon, tue: plan.tue, wed: plan.wed, thu: plan.thu,
        fri: plan.fri, sat: plan.sat, sun: plan.sun,
      },
    })
    if (res.ok) await setWeeklyPlan({ ...plan, syncStatus: 'synced' })
  } catch { /* keep pending, retry next time */ }
}

export async function pushAllPending(): Promise<void> {
  if (!_isLoggedIn) return
  await pushPendingSessions()
  await pushPendingCustomExercises()
  await pushPendingExercises()
  await pushPendingSettings()
  await pushPendingWeeklyPlan()
}

// ============================================================================
// Pull — fetch server state and merge
// ============================================================================

export interface SettingsConflict {
  exerciseKey: string
  local: LocalExerciseSettings
  server: LocalExerciseSettings
}

export interface ExercisesConflict {
  local: string[]  // exercise keys with pending local add/remove
  server: string[] // exercise keys from server
}

export interface PullConflicts {
  settings: SettingsConflict[]
  exercises: ExercisesConflict | null
}

export type ConflictResolver = (conflicts: PullConflicts) => Promise<'local' | 'server'>

/**
 * Pull the full server state and merge into local DB.
 * If there are pending local changes that diverge from server, `resolver` is
 * called once to ask the user which side to keep.
 */
export async function pullFromServer(resolver?: ConflictResolver): Promise<void> {
  const res = await syncApi.pull.$get()
  if (!res.ok) throw new Error(`Pull failed with status ${res.status}`)
  const data = await res.json()

  // Workout sessions: append-only, merge by id (server is authoritative for shared ids)
  const serverSessionIds = new Set(data.workoutSessions.map(s => s.id))
  const localSessions = await db.sessions.toArray()
  const sessionsToAdd: LocalSession[] = data.workoutSessions
    .filter(s => !localSessions.some(l => l.id === s.id))
    .map(s => ({
      id: s.id,
      exerciseKey: s.exerciseKey,
      exerciseType: s.exerciseType as 'aerobic' | 'anaerobic',
      startedAt: s.startedAt,
      endedAt: s.endedAt,
      durationSeconds: s.durationSeconds,
      summary: s.summary ? JSON.parse(s.summary) : null,
      createdAt: s.createdAt ?? new Date().toISOString(),
      syncStatus: 'synced',
    }))
  if (sessionsToAdd.length > 0) {
    await db.sessions.bulkAdd(sessionsToAdd)
  }
  // Local pending sessions not yet on server: keep them as-is (will push later)
  // Local synced sessions missing from server: extremely unusual (server lost data);
  // do nothing — user can re-trigger push via next save

  // Detect conflicts for exercises + settings
  const conflicts = await detectConflicts(data)

  let decision: 'local' | 'server' = 'server'
  if ((conflicts.settings.length > 0 || conflicts.exercises !== null) && resolver) {
    decision = await resolver(conflicts)
  }

  if (decision === 'server') {
    await applyServerState(data)
  } else {
    // Keep local; but still add missing sessions (already done) and save user info
    await setUser({ id: data.user.id, phone: data.user.phone, role: data.user.role })
  }

  if (!(await isBootstrapped())) {
    await markBootstrapped()
  }
}

async function detectConflicts(data: Awaited<ReturnType<Awaited<ReturnType<typeof syncApi.pull.$get>>['json']>>): Promise<PullConflicts> {
  const conflicts: PullConflicts = { settings: [], exercises: null }

  // Settings conflict: any local row with pending status where value differs from server
  const localSettings = await db.exerciseSettings.toArray()
  const serverByKey = new Map(data.exerciseSettings.map(s => [s.exerciseKey, s]))
  for (const l of localSettings) {
    if (l.syncStatus !== 'pending') continue
    const s = serverByKey.get(l.exerciseKey)
    if (!s) continue // server doesn't have this row, local is newer
    const fields: (keyof LocalExerciseSettings)[] = [
      'plankDuration', 'plankRestDuration', 'curlRestDuration', 'curlWeight',
      'pushUpRestDuration', 'dumbbellPressRestDuration', 'dumbbellPressWeight',
      'gluteBridgeRestDuration', 'squatRestDuration',
    ]
    const differs = fields.some(f => l[f] !== (s as unknown as LocalExerciseSettings)[f])
    if (differs) {
      conflicts.settings.push({
        exerciseKey: l.exerciseKey,
        local: l,
        server: { ...(s as unknown as LocalExerciseSettings), syncStatus: 'synced' as const },
      })
    }
  }

  // Exercises conflict: any local pending add/remove
  const localExercises = await db.userExercises.toArray()
  const hasPending = localExercises.some(e => e.syncStatus === 'pending')
  if (hasPending) {
    const localKeys = localExercises.filter(e => !e.pendingDelete).map(e => e.exerciseKey)
    const serverKeys = data.userExercises.map(e => e.exerciseKey)
    const differs = localKeys.length !== serverKeys.length || localKeys.some(k => !serverKeys.includes(k))
    if (differs) {
      conflicts.exercises = { local: localKeys, server: serverKeys }
    }
  }

  return conflicts
}

type PullJson = Awaited<ReturnType<Awaited<ReturnType<typeof syncApi.pull.$get>>['json']>>

async function applyServerState(data: PullJson): Promise<void> {
  // User info
  await setUser({ id: data.user.id, phone: data.user.phone, role: data.user.role })

  // Replace userExercises with server state
  await db.userExercises.clear()
  const newExercises: LocalUserExercise[] = data.userExercises.map(e => ({
    exerciseKey: e.exerciseKey,
    sortOrder: e.sortOrder,
    createdAt: e.createdAt ?? new Date().toISOString(),
    syncStatus: 'synced',
  }))
  // bulkPut so the apply step tolerates server rows with duplicate exerciseKey
  // (Dexie primary key is exerciseKey; a duplicate would throw ConstraintError)
  if (newExercises.length > 0) await db.userExercises.bulkPut(newExercises)

  // Replace exerciseSettings with server state
  await db.exerciseSettings.clear()
  const newSettings: LocalExerciseSettings[] = data.exerciseSettings.map(s => ({
    exerciseKey: s.exerciseKey,
    plankDuration: s.plankDuration ?? null,
    plankRestDuration: s.plankRestDuration ?? null,
    curlRestDuration: s.curlRestDuration ?? null,
    curlWeight: s.curlWeight ?? null,
    pushUpRestDuration: s.pushUpRestDuration ?? null,
    dumbbellPressRestDuration: s.dumbbellPressRestDuration ?? null,
    dumbbellPressWeight: s.dumbbellPressWeight ?? null,
    gluteBridgeRestDuration: s.gluteBridgeRestDuration ?? null,
    squatRestDuration: s.squatRestDuration ?? null,
    syncStatus: 'synced',
  }))
  if (newSettings.length > 0) await db.exerciseSettings.bulkPut(newSettings)

  // Replace customExercises with server state
  await db.customExercises.clear()
  const newCustoms: LocalCustomExercise[] = data.customExercises.map(c => ({
    exerciseKey: c.exerciseKey,
    name: c.name,
    icon: c.icon,
    template: c.template,
    exerciseType: c.exerciseType,
    restDuration: c.restDuration ?? null,
    defaultDuration: c.defaultDuration ?? null,
    weight: c.weight ?? null,
    quickReps1: c.quickReps1 ?? null,
    quickReps2: c.quickReps2 ?? null,
    quickReps3: c.quickReps3 ?? null,
    sortOrder: c.sortOrder,
    createdAt: c.createdAt ?? new Date().toISOString(),
    syncStatus: 'synced',
  }))
  if (newCustoms.length > 0) await db.customExercises.bulkPut(newCustoms)

  // Replace weekly plan with server state (or clear it if server has none)
  if (data.weeklyPlan) {
    const p = data.weeklyPlan
    const plan: LocalWeeklyPlan = {
      mon: p.mon ?? null, tue: p.tue ?? null, wed: p.wed ?? null,
      thu: p.thu ?? null, fri: p.fri ?? null, sat: p.sat ?? null,
      sun: p.sun ?? null,
      updatedAt: p.updatedAt ?? new Date().toISOString(),
      syncStatus: 'synced',
    }
    await setWeeklyPlan(plan)
  }
}

// ============================================================================
// Bootstrap — first-time pull after login
// ============================================================================

/** Ensures local DB is populated. Safe to call on every app start. */
export async function ensureBootstrapped(): Promise<void> {
  if (await isBootstrapped()) return
  await pullFromServer()
}

// ============================================================================
// Export / Import — plain JSON
// ============================================================================

export interface ExportFormat {
  version: 2
  exportedAt: string
  user: { phone: string } | null
  sessions: LocalSession[]
  userExercises: LocalUserExercise[]
  exerciseSettings: LocalExerciseSettings[]
  customExercises: LocalCustomExercise[]
}

export async function exportAllData(): Promise<ExportFormat> {
  const user = (await db.meta.get('user'))?.value as { phone: string } | undefined
  const [sessions, userExercises, exerciseSettings, customExercises] = await Promise.all([
    db.sessions.toArray(),
    db.userExercises.toArray(),
    db.exerciseSettings.toArray(),
    db.customExercises.toArray(),
  ])
  return {
    version: 2,
    exportedAt: new Date().toISOString(),
    user: user ? { phone: user.phone } : null,
    sessions, userExercises, exerciseSettings, customExercises,
  }
}

/**
 * Import data into local DB. Existing rows with same id/key are preserved
 * (imported data never overwrites). Imported rows are marked pending so the
 * next push uploads them to the server.
 */
export async function importData(payload: ExportFormat): Promise<{ added: number; skipped: number }> {
  const version = payload.version as number
  if (version !== 1 && version !== 2) {
    throw new Error('Unsupported export format')
  }
  let added = 0
  let skipped = 0

  await db.transaction('rw', [db.sessions, db.userExercises, db.exerciseSettings, db.customExercises], async () => {
    for (const s of payload.sessions) {
      const existing = await db.sessions.get(s.id)
      if (existing) { skipped++; continue }
      await db.sessions.add({ ...s, syncStatus: 'pending' })
      added++
    }
    for (const e of payload.userExercises) {
      const existing = await db.userExercises.get(e.exerciseKey)
      if (existing) { skipped++; continue }
      await db.userExercises.add({ ...e, syncStatus: 'pending', pendingDelete: false })
      added++
    }
    for (const s of payload.exerciseSettings) {
      const existing = await db.exerciseSettings.get(s.exerciseKey)
      if (existing) { skipped++; continue }
      await db.exerciseSettings.add({ ...s, syncStatus: 'pending' })
      added++
    }
    const customs = payload.customExercises ?? []
    for (const c of customs) {
      const existing = await db.customExercises.get(c.exerciseKey)
      if (existing) { skipped++; continue }
      await db.customExercises.add({ ...c, syncStatus: 'pending', pendingDelete: false })
      added++
    }
  })

  return { added, skipped }
}
